import { useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';

function buildChars(value: string, length: number): string[] {
  const arr = Array(length).fill('');
  for (let i = 0; i < Math.min(value.length, length); i++) arr[i] = value[i];
  return arr;
}

interface Props {
  length?: number;
  value: string;
  onChange: (val: string) => void;
  onComplete?: (val: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
}

/**
 * Input estilo OTP: N inputs de 1 dígito con auto-focus al siguiente.
 * Soporta paste de los N dígitos juntos.
 */
export function OtpInput({
  length = 4,
  value,
  onChange,
  onComplete,
  autoFocus,
  disabled,
}: Props) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const [chars, setChars] = useState<string[]>(() => buildChars(value, length));

  // Mantenemos los callbacks en refs para NO tenerlos como dependencias
  // del useEffect que dispara onComplete. Sin esto, cada re-render del padre
  // crea nuevas funciones inline, las deps cambian, y onComplete se vuelve
  // a disparar con el mismo codigo — lo que causaba un loop de validarOtp
  // cuando el padre re-renderizaba al mostrar el error.
  const onChangeRef = useRef(onChange);
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onChangeRef.current = onChange;
    onCompleteRef.current = onComplete;
  }, [onChange, onComplete]);

  // Si el padre cambia `value` externamente (ej. setOtp('') para resetear
  // tras un error), sincronizamos el estado interno. Sin esta sync, los
  // chars locales seguian completos y el useEffect de "complete" disparaba
  // de nuevo en el siguiente render.
  useEffect(() => {
    setChars((prev) => (prev.join('') === value ? prev : buildChars(value, length)));
  }, [value, length]);

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  // Disparamos onComplete una sola vez por codigo completo. Usamos un ref
  // para recordar el ultimo joined disparado y no reentrar si el padre
  // re-renderiza sin que el codigo cambie.
  const lastFiredRef = useRef<string | null>(null);
  useEffect(() => {
    const joined = chars.join('');
    onChangeRef.current(joined);
    const completo = joined.length === length && !chars.includes('');
    if (!completo) {
      lastFiredRef.current = null;
      return;
    }
    if (lastFiredRef.current === joined) return;
    lastFiredRef.current = joined;
    onCompleteRef.current?.(joined);
  }, [chars, length]);

  function setChar(idx: number, ch: string) {
    const clean = ch.replace(/\D/g, '').slice(0, 1);
    setChars((prev) => {
      const next = [...prev];
      next[idx] = clean;
      return next;
    });
    if (clean && idx < length - 1) refs.current[idx + 1]?.focus();
  }

  function handleKeyDown(idx: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      if (chars[idx]) {
        setChar(idx, '');
      } else if (idx > 0) {
        refs.current[idx - 1]?.focus();
        setChar(idx - 1, '');
      }
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      refs.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowRight' && idx < length - 1) {
      refs.current[idx + 1]?.focus();
    }
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(length).fill('');
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setChars(next);
    const focusIdx = Math.min(pasted.length, length - 1);
    refs.current[focusIdx]?.focus();
  }

  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        justifyContent: 'center',
        marginTop: 8,
      }}
    >
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          autoComplete="one-time-code"
          value={chars[i] ?? ''}
          onChange={(e) => setChar(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className="otpDigit"
        />
      ))}
    </div>
  );
}
